import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, Sparkles, Mic, Trash2, Edit2, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import EnhancedVoiceRecorder from "@/components/EnhancedVoiceRecorder";

interface PregnancyJournalProps {
  babyId: string;
}

const JOURNAL_PROMPTS = {
  pregnancy: [
    "How are you feeling today, physically and emotionally?",
    "What hopes and dreams do you have for your baby?",
    "Describe a moment today that made you smile.",
    "What are you most excited about in this journey?",
    "What challenges are you facing, and how are you coping?",
  ],
  postpartum: [
    "How are you adjusting to life with your baby?",
    "What moment today made you feel grateful?",
    "How are you taking care of yourself today?",
    "Describe your baby's personality emerging.",
    "What support do you need right now?",
  ],
  breastfeeding: [
    "How is your breastfeeding journey going?",
    "What strategies are working well for you?",
    "How do you feel during feeding times?",
    "What would you tell yourself a month ago?",
    "Celebrate a breastfeeding win today.",
  ],
  general: [
    "What made today special?",
    "Describe a milestone or achievement.",
    "What are you learning about yourself?",
    "Express gratitude for something today.",
    "What do you want to remember about this time?",
  ],
};

export function PregnancyJournal({ babyId }: PregnancyJournalProps) {
  const [journalType, setJournalType] = useState<keyof typeof JOURNAL_PROMPTS>("general");
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [entry, setEntry] = useState("");
  const [entries, setEntries] = useState<any[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<any[]>([]);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [audioTitle, setAudioTitle] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const { toast } = useToast();

  useEffect(() => {
    loadEntries();
  }, [babyId]);

  useEffect(() => {
    filterAndSearchEntries();
  }, [entries, searchQuery, filterType]);

  const filterAndSearchEntries = () => {
    let filtered = entries;
    
    if (filterType !== "all") {
      filtered = filtered.filter(e => e.journal_type === filterType);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.entry?.toLowerCase().includes(query) ||
        e.prompt?.toLowerCase().includes(query) ||
        e.audio_title?.toLowerCase().includes(query)
      );
    }
    
    setFilteredEntries(filtered);
  };

  const loadEntries = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("pregnancy_journal")
      .select("*")
      .eq("user_id", user.id)
      .eq("baby_id", babyId)
      .order("journal_date", { ascending: false });

    if (data) setEntries(data);
  };

  const handleSaveEntry = async () => {
    if (!entry.trim()) {
      toast({ title: "Empty entry", description: "Please write something", variant: "destructive" });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("pregnancy_journal")
      .insert({
        user_id: user.id,
        baby_id: babyId,
        journal_date: new Date().toISOString(),
        journal_type: journalType,
        prompt: selectedPrompt || null,
        entry: entry,
      });

    if (error) {
      toast({ title: "Error", description: "Failed to save entry", variant: "destructive" });
      return;
    }

    toast({ title: "Entry saved!", description: "Your journal entry has been saved" });
    setEntry("");
    setSelectedPrompt("");
    loadEntries();
  };

  const handleDeleteEntry = async (entryId: string) => {
    const { error } = await supabase
      .from("pregnancy_journal")
      .delete()
      .eq("id", entryId);

    if (error) {
      toast({ title: "Error", description: "Failed to delete entry", variant: "destructive" });
      return;
    }

    toast({ title: "Deleted", description: "Journal entry deleted" });
    loadEntries();
    setSelectedEntry(null);
  };

  const handleVoiceRecording = async (blob: Blob, url: string) => {
    if (!audioTitle.trim()) {
      toast({ title: "Title required", description: "Please enter a title for this audio", variant: "destructive" });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Convert blob to base64 for storage
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = async () => {
      const base64Audio = reader.result as string;
      
      const { error } = await supabase
        .from("pregnancy_journal")
        .insert({
          user_id: user.id,
          baby_id: babyId,
          journal_date: new Date().toISOString(),
          journal_type: journalType,
          entry: "",
          voice_recording_url: base64Audio,
          audio_title: audioTitle,
        });

      if (error) {
        toast({ title: "Error", description: "Failed to save audio", variant: "destructive" });
        return;
      }

      toast({ title: "Audio saved!", description: "Your voice recording has been saved" });
      setShowVoiceRecorder(false);
      setAudioTitle("");
      loadEntries();
    };
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-secondary/30">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          Why Journal This Journey?
        </h3>
        <p className="text-sm text-muted-foreground">
          Journaling helps you process emotions, remember precious moments, track your healing, and create a 
          meaningful record for your child. It's a space for honesty, reflection, and celebration. Years from 
          now, you'll treasure these words as a window into this transformative time in your life.
        </p>
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold mb-4">New Journal Entry</h3>
        <div className="space-y-4">
          <div>
            <Label>Journal Type</Label>
            <Select value={journalType} onValueChange={(value: any) => setJournalType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="pregnancy">Pregnancy</SelectItem>
                <SelectItem value="postpartum">Postpartum</SelectItem>
                <SelectItem value="breastfeeding">Breastfeeding</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Writing Prompt (Optional)
            </Label>
            <Select value={selectedPrompt} onValueChange={setSelectedPrompt}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a prompt or write freely" />
              </SelectTrigger>
              <SelectContent>
                {JOURNAL_PROMPTS[journalType].map((prompt, index) => (
                  <SelectItem key={index} value={prompt}>
                    {prompt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPrompt && (
            <Card className="p-3 bg-primary/5 border-primary/20">
              <p className="text-sm font-medium">{selectedPrompt}</p>
            </Card>
          )}

          <div>
            <Label>Your Entry</Label>
            <Textarea
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              placeholder="Write your thoughts, feelings, memories..."
              rows={8}
              className="resize-none"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSaveEntry} className="flex-1">
              Save Entry
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowVoiceRecorder(!showVoiceRecorder)}
            >
              <Mic className="h-4 w-4" />
            </Button>
          </div>

          {showVoiceRecorder && (
            <Card className="p-4 space-y-4">
              <div>
                <Label>Audio Title</Label>
                <Input
                  value={audioTitle}
                  onChange={(e) => setAudioTitle(e.target.value)}
                  placeholder="Give this recording a title..."
                />
              </div>
              <EnhancedVoiceRecorder 
                onRecordingComplete={handleVoiceRecording}
                onRecordingDelete={() => setAudioTitle("")}
              />
            </Card>
          )}
        </div>
      </Card>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Past Entries</h3>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search entries..."
              className="pl-9"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="pregnancy">Pregnancy</SelectItem>
              <SelectItem value="postpartum">Postpartum</SelectItem>
              <SelectItem value="breastfeeding">Breastfeeding</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filteredEntries.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>
              {entries.length === 0 
                ? "No journal entries yet. Start writing to capture this journey!"
                : "No entries match your search"}
            </p>
          </Card>
        ) : (
          filteredEntries.map((journalEntry) => (
            <Card 
              key={journalEntry.id} 
              className="p-4 cursor-pointer hover:border-primary transition-colors"
              onClick={() => setSelectedEntry(journalEntry)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium capitalize">
                  {journalEntry.journal_type}
                </span>
                <span className="text-sm text-muted-foreground">
                  {format(new Date(journalEntry.journal_date), "MMM d, yyyy 'at' h:mm a")}
                </span>
              </div>
              {journalEntry.audio_title && (
                <p className="text-sm font-semibold mb-2">🎵 {journalEntry.audio_title}</p>
              )}
              {journalEntry.prompt && (
                <p className="text-sm text-primary mb-2">Prompt: {journalEntry.prompt}</p>
              )}
              {journalEntry.entry && (
                <p className="text-sm whitespace-pre-wrap line-clamp-3">{journalEntry.entry}</p>
              )}
            </Card>
          ))
        )}
      </div>

      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="capitalize">{selectedEntry?.journal_type} Entry</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteEntry(selectedEntry?.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {format(new Date(selectedEntry.journal_date), "MMMM d, yyyy 'at' h:mm a")}
              </p>
              {selectedEntry.audio_title && (
                <div className="space-y-2">
                  <h4 className="font-semibold">🎵 {selectedEntry.audio_title}</h4>
                  {selectedEntry.voice_recording_url && (
                    <audio controls className="w-full">
                      <source src={selectedEntry.voice_recording_url} />
                      Your browser does not support the audio element.
                    </audio>
                  )}
                </div>
              )}
              {selectedEntry.prompt && (
                <Card className="p-3 bg-primary/5 border-primary/20">
                  <p className="text-sm font-medium">{selectedEntry.prompt}</p>
                </Card>
              )}
              {selectedEntry.entry && (
                <div className="whitespace-pre-wrap">{selectedEntry.entry}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
